import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, Layers } from 'lucide-react';
import { fetchGradeSubjects, SubjectDto } from '../services/gradeApi';

type LocationState = { gradeTitle?: string; gradeCode?: string };

const GradeSubjectsPage: React.FC = () => {
  const { gradeId } = useParams<{ gradeId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state as LocationState | undefined;

  const [subjects, setSubjects] = useState<SubjectDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const gradeTitle = navState?.gradeTitle || 'المرحلة';
  const gradeCode = navState?.gradeCode;

  useEffect(() => {
    if (!gradeId) {
      setError('معرّف المرحلة غير صالح');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const result = await fetchGradeSubjects(gradeId);
      if (cancelled) return;
      if (!result.ok) {
        setError(result.message || 'فشل تحميل المواد');
        setSubjects([]);
      } else {
        setSubjects(result.subjects);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [gradeId]);

  const goToGradeCourses = () => {
    if (!gradeId) return;
    navigate(`/courses/grade/${encodeURIComponent(gradeId)}`, {
      state: { grade: gradeCode, gradeTitle },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50/30 to-slate-50" dir="rtl">
      <div className="border-b border-emerald-100 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-4" dir="ltr">
            <Link to="/" className="hover:text-emerald-600 transition-colors">
              الرئيسية
            </Link>
            <ArrowRight className="w-4 h-4 text-slate-400 rotate-180" aria-hidden />
            <span className="text-slate-800 font-medium text-right" dir="rtl">
              مواد {gradeTitle}
            </span>
          </nav>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="font-el-messiri text-2xl sm:text-3xl font-bold text-slate-900">
                مواد <span className="text-emerald-700">{gradeTitle}</span>
              </h1>
              <p className="font-cairo text-slate-600 mt-2 text-sm">
                قائمة المواد المرتبطة بهذه المرحلة من الخادم
              </p>
            </div>
            <button
              type="button"
              onClick={goToGradeCourses}
              className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white bg-emerald-900/80 border-2 border-amber-400/60 shadow-md transition hover:bg-emerald-900 font-cairo shrink-0"
            >
              <BookOpen className="h-4 w-4" aria-hidden />
              مناهج المرحلة
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading && (
          <div className="text-center py-16 font-cairo text-slate-500">جاري تحميل المواد...</div>
        )}

        {!loading && error && (
          <div className="max-w-lg mx-auto text-center rounded-2xl border border-red-100 bg-red-50/80 px-6 py-8">
            <p className="font-cairo text-red-800 text-sm mb-4">{error}</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link
                to="/login"
                className="inline-flex rounded-lg border border-emerald-600 px-4 py-2 text-sm font-cairo text-emerald-800 hover:bg-emerald-50"
              >
                تسجيل الدخول
              </Link>
              <Link to="/" className="inline-flex rounded-lg bg-emerald-700 px-4 py-2 text-sm font-cairo text-white hover:bg-emerald-800">
                العودة للرئيسية
              </Link>
            </div>
          </div>
        )}

        {!loading && !error && subjects.length === 0 && (
          <div className="text-center py-16 rounded-2xl border border-emerald-100 bg-white shadow-sm">
            <Layers className="h-12 w-12 text-emerald-200 mx-auto mb-3" aria-hidden />
            <p className="font-cairo text-slate-600">لا توجد مواد مفعّلة لهذه المرحلة حالياً</p>
            <button
              type="button"
              onClick={goToGradeCourses}
              className="mt-6 font-cairo text-sm text-emerald-700 font-semibold hover:underline"
            >
              الانتقال إلى مناهج المرحلة
            </button>
          </div>
        )}

        {!loading && !error && subjects.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {subjects.map((s) => (
              <Link
                key={s.id}
                to={`/subjects/${encodeURIComponent(s.id)}/courses`}
                state={{
                  subjectName: s.name,
                  gradeId,
                  gradeTitle,
                  gradeCode,
                }}
                className="block rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm transition-all hover:border-emerald-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h2 className="font-el-messiri text-lg font-bold text-slate-900 leading-snug">{s.name}</h2>
                  {s.code ? (
                    <span className="font-cairo text-[10px] uppercase tracking-wide text-slate-400 shrink-0">
                      {s.code}
                    </span>
                  ) : null}
                </div>
                {s.description ? (
                  <p className="font-cairo text-sm text-slate-600 leading-relaxed line-clamp-4 mb-4">{s.description}</p>
                ) : (
                  <div className="mb-4" />
                )}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-3 text-xs font-cairo text-slate-500">
                    {s.courseCount > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-800 ring-1 ring-emerald-100">
                        <BookOpen className="h-3.5 w-3.5" aria-hidden />
                        {s.courseCount} منهج
                      </span>
                    ) : null}
                    {s.teacherCount > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-amber-900 ring-1 ring-amber-100">
                        <Users className="h-3.5 w-3.5" aria-hidden />
                        {s.teacherCount} معلم
                      </span>
                    ) : null}
                  </div>
                  <span className="font-cairo text-sm font-semibold text-emerald-700">عرض المناهج</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GradeSubjectsPage;
